import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, TextField, Paper, Link } from '@mui/material';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import type { GridColDef, GridPaginationModel, GridSortModel, GridFilterModel } from '@mui/x-data-grid';
import type { FilterModel } from '@lingaai/types';
import { Plus } from 'lucide-react';
import { useNodes, usePicklists, useUsers } from '@lingaai/shared-hooks';
import { formatDate, formatDateTime, formatNumber } from '@lingaai/shared-utils';
import { NODE_REGISTRY as NODE_TYPE_CONFIG } from '@lingaai/types/graph/nodes';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';
import { NodeDrawer } from './NodeDrawer';

interface NodeListViewProps {
  type: string;
  filter?: Record<string, string>;
  title?: string;
}

export function NodeListView({ type, filter, title }: NodeListViewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const apiRef = useGridApiRef();
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 100,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'created_at', sort: 'desc' }]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [isStateRestored, setIsStateRestored] = useState(false);

  const stateKey = `datagrid-state-${type}`;

  const nodeConfig = NODE_TYPE_CONFIG[type];
  if (!nodeConfig) {
    return <Typography color="error">Unknown node type: {type}</Typography>;
  }

  // Convert MUI GridFilterModel to backend FilterModel
  const backendFilterModel: FilterModel | undefined = useMemo(() => {
    if (!filterModel.items.length) return undefined;

    const model = {
      items: filterModel.items.map((item) => ({
        field: item.field,
        operator: item.operator as any,
        value: item.value,
      })),
      logicOperator: filterModel.logicOperator as 'and' | 'or' | undefined,
    };
    console.log('🔍 Filter Model:', { filterModel, backendFilterModel: model });
    return model;
  }, [filterModel]);

  // Fetch nodes from backend with server-side pagination and sorting
  const sortField = sortModel[0]?.field || 'created_at';
  const sortOrder = sortModel[0]?.sort || 'desc';

  // System fields (created_at, updated_at, id) should not be prefixed with props.
  const systemFields = ['created_at', 'updated_at', 'id'];
  const sortByField = systemFields.includes(sortField) || sortField.startsWith('props.') ? sortField : `props.${sortField}`;

  const { data: nodesResponse, isLoading } = useNodes(type, {
    search: search || undefined,
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    filterModel: backendFilterModel,
    sortBy: sortByField,
    sortOrder,
    includes: nodeConfig.listViewIncludes,
  });

  // Apply client-side filter if provided (for route-based filtering like "portfolio companies")
  const nodes = nodesResponse?.data || [];
  const filteredNodes = filter
    ? nodes.filter((node) => {
        return Object.entries(filter).every(([key, value]) => node.props[key] === value);
      })
    : nodes;

  const displayTitle = title || nodeConfig.displayNamePlural;
  const totalRows = nodesResponse?.pagination?.total || 0;

  // Fetch picklists for label lookup
  const { data: allPicklists } = usePicklists();
  const { data: allUsers } = useUsers();

  // Build picklist lookup map: { picklistName: { id: label } }
  const picklistLabelMap = useMemo(() => {
    const map: Record<string, Record<string, string>> = {};
    if (allPicklists) {
      for (const picklist of allPicklists) {
        const picklistMap: Record<string, string> = {};
        const items = picklist.items as Array<{ id: string; label: string }>;
        for (const item of items) {
          picklistMap[item.id] = item.label;
        }
        map[picklist.name] = picklistMap;
      }
    }
    return map;
  }, [allPicklists]);

  // Build field-to-picklist map and field-to-type map from nodeConfig
  const fieldPicklistMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const field of nodeConfig.formFields) {
      if (field.picklistName) {
        map[field.key] = field.picklistName;
      }
    }
    return map;
  }, [nodeConfig]);

  // Build user name lookup map
  const userNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (allUsers) {
      for (const user of allUsers) {
        map[user.id] = user.full_name || user.email;
      }
    }
    return map;
  }, [allUsers]);

  // Restore state from sessionStorage on mount
  useEffect(() => {
    if (!isStateRestored && apiRef.current) {
      try {
        const savedState = sessionStorage.getItem(stateKey);
        if (savedState) {
          const parsedState = JSON.parse(savedState);
          apiRef.current.restoreState(parsedState);

          // Also restore filterModel to component state
          if (parsedState.filter?.filterModel) {
            setFilterModel(parsedState.filter.filterModel);
          }
        }
      } catch (error) {
        console.error('Failed to restore DataGrid state:', error);
      }
      setIsStateRestored(true);
    }
  }, [apiRef, stateKey, isStateRestored]);

  // Save state to sessionStorage on changes
  useEffect(() => {
    if (!isStateRestored || !apiRef.current) return;

    const handleStateChange = () => {
      if (!apiRef.current) return;
      try {
        const currentState = apiRef.current.exportState();
        sessionStorage.setItem(stateKey, JSON.stringify(currentState));
      } catch (error) {
        console.error('Failed to save DataGrid state:', error);
      }
    };

    // Subscribe to state changes
    const unsubscribe = apiRef.current.subscribeEvent('stateChange', handleStateChange);

    return () => {
      unsubscribe();
    };
  }, [apiRef, stateKey, isStateRestored]);

  // Build columns from formFields
  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = nodeConfig.formFields
      .filter((field) => field.type !== 'lookup')
      .map((field) => {
        const col: GridColDef = {
          field: field.key,
          headerName: field.label,
          flex: field.key === 'name' ? 1 : 0,
          minWidth: field.key === 'name' || field.key === 'title' || field.key === 'subject' ? 200 : 150,
          valueGetter: (_value: unknown, row: { props: Record<string, unknown>; _includes?: Record<string, any> }) => {
            // Handle included data (e.g., organization.name, primary_contact.name)
            if (field.key.includes('.') && !field.key.startsWith('geo.')) {
              const parts = field.key.split('.');
              const includeAlias = parts[0] ?? '';
              const propPath = parts[1] ?? '';
              const includedData = row._includes?.[includeAlias];
              if (includedData) {
                return includedData.props?.[propPath] || '';
              }
            }
            // Handle virtual fields (dot notation like geo.all, geo.city)
            if (field.key.includes('.')) {
              const parts = field.key.split('.');
              const parent = parts[0] ?? '';
              const sub = parts[1] ?? '';
              const obj = row.props[parent] as Record<string, string> | undefined;
              if (!obj) return '';
              if (sub === 'all') {
                return [obj.city, obj.state, obj.country].filter(Boolean).join(', ');
              }
              return obj[sub] ?? '';
            }
            const rawValue = row.props[field.key];
            // Handle geo field - format as "City, State, Country"
            if (field.key === 'geo' && rawValue && typeof rawValue === 'object') {
              const geo = rawValue as { city?: string; state?: string; country?: string };
              return [geo.city, geo.state, geo.country].filter(Boolean).join(', ');
            }
            // For picklist fields configured as singleSelect, return raw ID
            // DataGrid will automatically look up the label from valueOptions
            if (field.picklistName) {
              return rawValue;
            }
            // Look up user name if this field is a user field
            if (field.type === 'user' && rawValue && typeof rawValue === 'string') {
              return userNameMap[rawValue] ?? rawValue;
            }
            return rawValue;
          },
        };

        // Configure picklist columns with dropdown filters
        if (field.picklistName) {
          const picklistItems = picklistLabelMap[field.picklistName];

          if (picklistItems) {
            col.type = 'singleSelect';
            (col as any).valueOptions = Object.entries(picklistItems).map(([id, label]) => ({
              value: id,
              label: label,
            }));
          }
        }
        // Make included data columns clickable
        if (field.key.includes('.') && !field.key.startsWith('geo.')) {
          col.renderCell = (params: any) => {
            const parts = field.key.split('.');
            const alias = parts[0];
            if (!alias) return params.value || '';

            const includedNode = params.row._includes?.[alias];

            if (!includedNode || !params.value) return params.value || '';

            // Use displayNamePlural from node config for proper pluralization
            const nodeConfig = NODE_TYPE_CONFIG[includedNode.type];
            const pluralPath = nodeConfig?.displayNamePlural.toLowerCase() || `${includedNode.type.toLowerCase()}s`;

            return (
              <Link
                component="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/${pluralPath}/${includedNode.id}`);
                }}
                sx={{ textAlign: 'left', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {params.value}
              </Link>
            );
          };
        }

        // Format datetime/date fields
        if (field.type === 'datetime') {
          col.valueFormatter = (value: string) => formatDateTime(value);
        } else if (field.type === 'date') {
          col.valueFormatter = (value: string) => formatDate(value);
        } else if (field.type === 'number') {
          col.valueFormatter = (value: number) => formatNumber(value, field.format);
        }
        return col;
      });
    // Add created_at column
    cols.push({
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueFormatter: (value: string) => formatDate(value),
    });
    return cols;
  }, [nodeConfig, fieldPicklistMap, picklistLabelMap]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{displayTitle}</Typography>
        <Button variant="contained" startIcon={<Plus size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />} onClick={() => setDrawerOpen(true)}>
          Create {nodeConfig.displayName}
        </Button>
      </Box>

      <Paper sx={{ mb: 3, p: 2 }}>
        <TextField fullWidth placeholder={`Search ${nodeConfig.displayNamePlural.toLowerCase()}...`} value={search} onChange={(e) => setSearch(e.target.value)} size="small" />
      </Paper>

      <Paper sx={{ height: 'calc(100vh - 250px)' }}>
        <DataGrid
          apiRef={apiRef}
          rows={filteredNodes}
          columns={columns}
          loading={isLoading}
          paginationMode="server"
          rowCount={totalRows}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          pageSizeOptions={[25, 50, 100]}
          onRowClick={(params) => navigate(`${location.pathname}/${params.id}`)}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </Paper>

      <NodeDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} nodeType={type} mode="create" />
    </Box>
  );
}
